if (!customElements.get('ingredients-carousel')) {
  class IngredientsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ingredients-stylized__carousel-track');
      this.slides = Array.from(this.querySelectorAll('.ingredients-stylized__carousel-slide'));
      this.contents = Array.from(this.querySelectorAll('.ingredients-stylized__content-slide'));
      this.prevBtn = this.querySelector('.ingredients-stylized__carousel-btn--prev');
      this.nextBtn = this.querySelector('.ingredients-stylized__carousel-btn--next');
      this.column = this.querySelector('.ingredients-stylized__carousel-column');
      this.slideWidth = 150;
      this.index = 0;
      this.pos = 0;
      this.raf = null;

      if (!this.track || this.slides.length === 0) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.arrow(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.arrow(1));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.arrow(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.arrow(1);
        }
      });
      this.setupDrag();
      this.goTo(0);
    }

    stopGlide() {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    arrow(direction) {
      this.stopGlide();
      this.track.classList.remove('ingredients-stylized__carousel-track--dragging');
      this.goTo(this.index + direction);
    }

    // Class-driven layout: CSS transitions animate the fan into place.
    goTo(index) {
      const names = ['far-prev', 'prev', 'active', 'next', 'far-next'];
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.pos = this.index * this.slideWidth;
      this.slides.forEach((slide, n) => {
        names.forEach((name) => slide.classList.remove('ingredients-stylized__carousel-slide--' + name));
        const distance = n - this.index;
        if (Math.abs(distance) <= 2) slide.classList.add('ingredients-stylized__carousel-slide--' + names[distance + 2]);
        slide.style.transform = '';
        slide.style.opacity = '';
        slide.style.zIndex = '';
      });
      this.syncContent();
    }

    syncContent() {
      this.contents.forEach((content, n) => {
        content.classList.toggle('ingredients-stylized__content-slide--active', n === this.index);
      });
      if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
    }

    // Paints every slide for a fractional position (px along the virtual strip),
    // interpolating between the CSS keyframe layouts. Used while dragging and
    // during the momentum glide, with transitions disabled.
    renderAt(position) {
      const max = (this.slides.length - 1) * this.slideWidth;
      position = Math.max(0, Math.min(max, position));
      this.slides.forEach((slide, n) => {
        const d = (n * this.slideWidth - position) / this.slideWidth;
        const a = Math.abs(d);
        const sign = d < 0 ? -1 : 1;
        let x, scale, opacity;
        if (a <= 1) {
          x = 120 * d;
          scale = 1 - 0.3 * a;
          opacity = 1 - 0.6 * a;
        } else if (a <= 2) {
          x = sign * (120 + 80 * (a - 1));
          scale = 0.7 - 0.2 * (a - 1);
          opacity = 0.4 - 0.2 * (a - 1);
        } else {
          x = sign * (200 + 50 * (a - 2));
          scale = 0.5;
          opacity = 0;
        }
        slide.style.transform = 'translateX(' + x + 'px) scale(' + scale + ')';
        slide.style.opacity = opacity;
        slide.style.zIndex = a < 0.5 ? 10 : a < 1.5 ? 3 : a < 2.5 ? 2 : 1;
      });
      const nearest = Math.round(position / this.slideWidth);
      if (nearest !== this.index) {
        this.index = nearest;
        this.syncContent();
      }
      this.pos = position;
      return position;
    }

    // Momentum glide after release: friction decay, then ease into the snap.
    // One frame-rate-independent loop replaces the old theme's duplicated
    // touch/mouse momentum engines.
    glide(releaseVelocity) {
      this.stopGlide();
      const friction = 0.94;
      let vel = releaseVelocity * 16; // px per 60fps frame, like the old engine
      let last = performance.now();
      const step = (now) => {
        const dt = Math.min((now - last) / 16.67, 3);
        last = now;
        const max = (this.slides.length - 1) * this.slideWidth;
        if (Math.abs(vel) >= 8) {
          vel *= Math.pow(friction, dt);
          let next = this.pos + vel * dt;
          if (next <= 0 || next >= max) {
            next = Math.max(0, Math.min(max, next));
            vel = 0;
          }
          this.renderAt(next);
        } else {
          const target = Math.round(this.pos / this.slideWidth) * this.slideWidth;
          const dist = target - this.pos;
          if (Math.abs(dist) < 0.5) {
            this.raf = null;
            this.track.classList.remove('ingredients-stylized__carousel-track--dragging');
            // renderAt at an integer position matches the class layout exactly,
            // so swapping back to classes causes no visual jump.
            this.goTo(Math.round(target / this.slideWidth));
            return;
          }
          this.renderAt(this.pos + dist * Math.min(0.25 * dt, 1));
        }
        this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }

    setupDrag() {
      const column = this.column;
      if (!column) return;
      let active = false;
      let dragging = false;
      let startX = 0;
      let startPosition = 0;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;

      column.addEventListener('pointerdown', (e) => {
        active = true;
        dragging = false;
        startX = lastX = e.clientX;
        // Grab wherever the strip currently is — including mid-glide.
        this.stopGlide();
        startPosition = this.pos;
        lastTime = performance.now();
        velocity = 0;
      });

      column.addEventListener('pointermove', (e) => {
        if (!active) return;
        const dx = e.clientX - startX;
        if (!dragging) {
          if (Math.abs(dx) < 8) return;
          dragging = true;
          column.setPointerCapture(e.pointerId);
          this.track.classList.add('ingredients-stylized__carousel-track--dragging');
        }
        const now = performance.now();
        const dt = now - lastTime;
        if (dt > 0) velocity = (lastX - e.clientX) / dt;
        this.renderAt(startPosition - dx);
        lastX = e.clientX;
        lastTime = now;
      });

      const release = () => {
        if (!active) return;
        active = false;
        if (!dragging) return;
        dragging = false;
        this.glide(velocity);
      };
      column.addEventListener('pointerup', release);
      column.addEventListener('pointercancel', release);

      column.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }
  customElements.define('ingredients-carousel', IngredientsCarousel);
}
